import { useState } from "react";
import type { ReviewListFilter, ReviewOverviewResponse, ReviewProductRow } from "../../types/reviews";
import { RatingDist } from "./components/RatingDist";
import { TrendChart } from "./components/TrendChart";
import { StoreCards } from "./components/StoreCards";

interface OverviewTabProps {
  data: ReviewOverviewResponse;
  onGoToResponse: (filter: ReviewListFilter) => void;
}

type SortKey = "low" | "high" | "count";

function deltaMeta(value: number | null, invert = false): { text: string; cls: string } {
  if (value === null) return { text: "―", cls: "muted" };
  const good = invert ? value < 0 : value > 0;
  const cls = value === 0 ? "muted" : good ? "good" : "bad";
  const sign = value > 0 ? "+" : "";
  return { text: `${sign}${value}`, cls };
}

export function OverviewTab({ data, onGoToResponse }: OverviewTabProps) {
  const [sort, setSort] = useState<SortKey>("low");
  const { kpis, dist, trend, stores, products, low } = data;

  const sortedProducts = products.slice().sort((a, b) => {
    if (sort === "low") return a.avg - b.avg;
    if (sort === "high") return b.avg - a.avg;
    return b.count - a.count;
  });

  const lowAlerts = products.filter((p) => p.dist[3] + p.dist[4] >= 2);

  const avgDelta = deltaMeta(kpis.avgRatingDelta);
  const countDelta = deltaMeta(kpis.countDeltaPct);
  const fillDelta = deltaMeta(kpis.fillRateDeltaPt);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="rv-kpi-grid">
        <div className="rv-kpi-card">
          <span className="rv-kpi-label">平均評価</span>
          <span className="rv-kpi-value">{kpis.avgRating !== null ? `${kpis.avgRating.toFixed(1)} / 5` : "―"}</span>
          <span className={`rv-kpi-delta ${avgDelta.cls}`}>前期比 {avgDelta.text}</span>
        </div>
        <div className="rv-kpi-card">
          <span className="rv-kpi-label">レビュー件数</span>
          <span className="rv-kpi-value">{kpis.count.toLocaleString("ja-JP")}</span>
          <span className={`rv-kpi-delta ${countDelta.cls}`}>前期比 {countDelta.text === "―" ? "―" : `${countDelta.text}%`}</span>
        </div>
        <div className="rv-kpi-card">
          <span className="rv-kpi-label">商品レビュー記入率</span>
          <span className="rv-kpi-value">{kpis.fillRate !== null ? `${kpis.fillRate}%` : "―"}</span>
          <span className={`rv-kpi-delta ${fillDelta.cls}`}>前期比 {fillDelta.text === "―" ? "―" : `${fillDelta.text}pt`}</span>
        </div>
        <div className="rv-kpi-card">
          <span className="rv-kpi-label">低評価（★1〜2）</span>
          <span className="rv-kpi-value">{kpis.lowCount}</span>
          <span className="rv-kpi-delta bad">未対応 {kpis.lowOpen}件</span>
        </div>
      </div>

      <div className="rv-two-col">
        <div className="rv-card">
          <span className="rv-card-title">評価分布</span>
          <RatingDist dist={dist} />
        </div>
        <div className="rv-card">
          <span className="rv-card-title">平均評価と商品レビュー記入率の推移</span>
          <TrendChart trend={trend} />
        </div>
      </div>

      <StoreCards stores={stores} />

      <div className="rv-card">
        <div className="rv-card-head">
          <span className="rv-card-title">商品別レビュー点数</span>
          <div style={{ display: "flex", gap: 12 }}>
            <button className={`rv-prod-sort-btn ${sort === "low" ? "active" : ""}`} onClick={() => setSort("low")}>
              低い順
            </button>
            <button className={`rv-prod-sort-btn ${sort === "high" ? "active" : ""}`} onClick={() => setSort("high")}>
              高い順
            </button>
            <button className={`rv-prod-sort-btn ${sort === "count" ? "active" : ""}`} onClick={() => setSort("count")}>
              件数順
            </button>
          </div>
        </div>
        {sortedProducts.length === 0 ? (
          <div className="rv-empty">該当期間の商品レビューはありません。</div>
        ) : (
          <div className="rv-prod-table">
            <div className="rv-prod-head">
              <span>商品名</span>
              <span>評価分布</span>
              <span>平均</span>
              <span>件数</span>
              <span>前月比</span>
            </div>
            {sortedProducts.map((p) => (
              <ProductRow key={p.name} row={p} />
            ))}
          </div>
        )}
      </div>

      <div className="rv-card">
        <div className="rv-card-head">
          <span className="rv-card-title">商品の低評価 対応状況</span>
          <button className="rv-btn" onClick={() => onGoToResponse("low")}>
            一覧へ →
          </button>
        </div>
        <div className="rv-pipeline">
          <button className="rv-pipeline-seg" onClick={() => onGoToResponse("cause")}>
            <span className="rv-pipeline-bar" style={{ background: "var(--rv-bad)", width: `${barWidth(low.open, low.total)}%` }} />
            <span className="rv-pipeline-value">{low.open}</span>
            <span className="rv-pipeline-label">未対応（原因分類前）</span>
          </button>
          <button className="rv-pipeline-seg" onClick={() => onGoToResponse("resp")}>
            <span className="rv-pipeline-bar" style={{ background: "var(--rv-warn-light)", width: `${barWidth(low.wip, low.total)}%` }} />
            <span className="rv-pipeline-value">{low.wip}</span>
            <span className="rv-pipeline-label">対応中</span>
          </button>
          <button className="rv-pipeline-seg" onClick={() => onGoToResponse("done")}>
            <span className="rv-pipeline-bar" style={{ background: "var(--rv-good)", width: `${barWidth(low.done, low.total)}%` }} />
            <span className="rv-pipeline-value">{low.done}</span>
            <span className="rv-pipeline-label">完了</span>
          </button>
        </div>
        <div className="rv-store-mini-grid">
          <div className="rv-store-mini-item">
            <span className="rv-store-mini-label">自動一次返信率</span>
            <span className="rv-store-mini-value">{low.autoReplyRate !== null ? `${low.autoReplyRate}%` : "―"}</span>
          </div>
          <div className="rv-store-mini-item">
            <span className="rv-store-mini-label">平均完了時間</span>
            <span className="rv-store-mini-value">{low.avgCompleteHours !== null ? `${low.avgCompleteHours}h` : "―"}</span>
          </div>
        </div>
        {low.overdue > 0 && (
          <button className="rv-alert" style={{ cursor: "pointer", border: "none", textAlign: "left" }} onClick={() => onGoToResponse("overdue")}>
            <strong>個別対応期限（{low.slaHours}h）超過 {low.overdue}件</strong>
            <span>クリックして一覧を確認してください。</span>
          </button>
        )}
        {lowAlerts.length > 0 && (
          <div className="rv-alert">
            <strong>同一商品で低評価が続いています</strong>
            {lowAlerts.map((p) => (
              <span key={p.name}>
                {p.name}：期間内に★1〜2が{p.dist[3] + p.dist[4]}件
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function barWidth(n: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((n / total) * 100));
}

function ProductRow({ row }: { row: ReviewProductRow }) {
  const total = row.dist.reduce((s, n) => s + n, 0) || 1;
  const colors = ["var(--rv-good)", "var(--rv-good-light)", "var(--rv-neutral)", "var(--rv-warn-light)", "var(--rv-bad)"];
  const prevDeltaText =
    row.prevAvg !== null ? `${row.avg - row.prevAvg >= 0 ? "+" : ""}${(row.avg - row.prevAvg).toFixed(1)}` : "―";
  return (
    <div className="rv-prod-row">
      <span>{row.name}</span>
      <span className="rv-prod-dist">
        {row.dist.map((n, i) => (n > 0 ? <span key={i} style={{ width: `${(n / total) * 100}%`, background: colors[i] }} /> : null))}
      </span>
      <span className={row.avg < 4 ? "rv-prod-avg low" : "rv-prod-avg"}>{row.avg.toFixed(1)}</span>
      <span>{row.count}</span>
      <span>{prevDeltaText}</span>
    </div>
  );
}
